// store/v3/table.ts — live POS table state + bill items (SPEC §3 TableStoreApi)
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { TableDoc, TableItem, TableState, TableSummary } from '~/types/v3/entities'
import type { TableStoreApi } from '~/types/v3/stores'
import { combineTableItems, computeTableSummary } from '~/utils/v3/summary'

export const useTableStore = defineStore('v3-table', () => {
  const state = ref<TableState | null>(null)
  const items = ref<TableItem[]>([])

  /** Strictly `true` — anything else (missing node, 0, 'false') leaves the table open. */
  const isLocked = computed<boolean>(() => state.value?.lock === true)

  /** Legacy fallback: a table always has at least one guest. */
  const guests = computed<number>(() => {
    const value = Number(state.value?.guests)
    return Number.isFinite(value) && value >= 1 ? value : 1
  })

  const channelId = computed<string | null>(() => state.value?.channel?.channelId ?? null)

  const summary = computed<TableSummary>(() => computeTableSummary(items.value))

  const combinedItems = computed<TableItem[]>(() => combineTableItems(items.value))

  function setState(s: TableState | null): void {
    state.value = s ?? null
  }

  function setItems(d: TableDoc | null): void {
    items.value = d?.items ?? []
  }

  function reset(): void {
    state.value = null
    items.value = []
  }

  return {
    state, items,
    isLocked, guests, channelId, summary, combinedItems,
    setState, setItems, reset,
  } satisfies TableStoreApi
})
